<template>
    <div class="modal-card">
        <header class="modal-card-head has-background-white">
            <h3 class="modal-card-title has-text-centered">Subir mayor</h3>
            <button class="delete" aria-label="Cerrar Modal" @click="$emit('close')"></button>
        </header>
        <section class="modal-card-body box-body">
            <div class="px-5">
                <!-- Última subida del mayor -->
                <div
                    v-if="lastUpload && lastUpload.last_ledger_upload_at"
                    class="last-upload-box"
                >
                    <div class="last-upload-title">Última subida</div>
                    <div class="last-upload-date">
                        {{ formatDateTime(lastUpload.last_ledger_upload_at) }}
                    </div>
                    <div class="last-upload-stats">
                        <span>
                            <strong>{{ lastUpload.last_ledger_documents_created }}</strong>
                            documentos
                        </span>
                        <span>
                            <strong>{{ lastUpload.last_ledger_rows_parsed }}</strong>
                            filas
                        </span>
                    </div>
                </div>

                <!-- Fecha de corte (mayor <-> SII) -->
                <div class="is-size-6 has-text-weight-light mb-3">
                    <strong>Fecha de corte (mayor &harr; SII)</strong>
                    <br />
                    Hasta esta fecha el reporte usa el <strong>mayor</strong>; despu&eacute;s,
                    el <strong>SII</strong> (Fintoc). D&eacute;jala vac&iacute;a para usar solo SII,
                    o usa <strong>Solo mayor</strong> si la empresa no usa SII.
                </div>
                <b-field>
                    <b-datepicker
                        v-model="cutoffDate"
                        placeholder="Selecciona la fecha de corte"
                        icon="calendar-today"
                        locale="es-CL"
                        :editable="true"
                        :loading="loadingCutoff"
                        trap-focus
                        expanded
                    />
                </b-field>
                <p v-if="isLedgerOnly" class="help has-text-info mb-2">
                    Solo mayor: el reporte usa el mayor en todas las fechas.
                </p>
                <div class="buttons are-small is-justify-content-flex-end">
                    <b-button
                        class="is-info is-light"
                        :disabled="isLedgerOnly || savingCutoff"
                        @click="setLedgerOnly()"
                    >
                        Solo mayor
                    </b-button>
                    <b-button
                        class="is-light"
                        :disabled="!cutoffDate || savingCutoff"
                        @click="clearCutoff()"
                    >
                        Limpiar
                    </b-button>
                    <b-button
                        class="is-primary"
                        :disabled="savingCutoff"
                        :loading="savingCutoff"
                        @click="saveCutoff()"
                    >
                        Guardar
                    </b-button>
                </div>

                <div class="divisionLine"></div>

                <!-- Tipo de negocio -->
                <div class="is-size-6 has-text-weight-light mb-2">
                    <strong>Tipo de negocio</strong>
                </div>
                <b-field class="mb-4">
                    <b-radio-button v-model="businessType" native-value="Productos" type="is-primary is-light">
                        Productos
                    </b-radio-button>
                    <b-radio-button v-model="businessType" native-value="Servicios" type="is-primary is-light">
                        Servicios
                    </b-radio-button>
                </b-field>

                <!-- Planilla del mayor (.xlsx) -->
                <div class="is-size-6 has-text-weight-light mb-3">
                    <strong>Planilla del mayor (.xlsx)</strong>
                    <br />
                    Arrastra el Excel del libro mayor a esta &aacute;rea.
                </div>
                <UploadFile
                    fileName="planilla del mayor"
                    messageFormat="XLSX"
                    extensionAccept=".xlsx"
                    :isLoading="uploadingLedger"
                    @uploaded="onUploaded"
                    @removedFile="onRemovedFile"
                />
            </div>
        </section>
        <footer class="modal-card-foot bt-none has-background-white is-justify-content-flex-end">
            <b-button class="is-light mr-2" @click="$emit('close')">Cerrar</b-button>
            <b-button
                class="is-primary"
                aria-label="Subir mayor"
                :disabled="!fileLedger || uploadingLedger"
                :loading="uploadingLedger"
                @click="submitLedger()"
            >
                Subir
            </b-button>
        </footer>
    </div>
</template>

<script>
import UploadFile from "@/components/uploadFile/UploadFile";
import {
    getLedgerCutoff,
    setLedgerCutoff,
    uploadLedger,
    getLedgerLastUpload,
} from "@/api/carteraAnalista";
import { consoleHandlerError, notificate } from "@/utils/message";
import moment from "moment";

// Corte centinela "todo mayor": una fecha lejana (regla emitted_at <= corte →
// mayor) hace que la empresa muestre el mayor en todas las fechas sin tener que
// re-actualizar el corte. Empresas que solo usan mayor (sin SII/Fintoc).
const LEDGER_ONLY_CUTOFF = "2099-12-31";

export default {
    name: "UploadLedger",
    components: {
        UploadFile,
    },
    props: {
        idCompany: {
            type: [Number, String],
            required: true,
            default: null,
        },
    },
    data() {
        return {
            cutoffDate: null,
            loadingCutoff: false,
            savingCutoff: false,
            fileLedger: null,
            uploadingLedger: false,
            lastUpload: null,
            businessType: null,
        };
    },
    created() {
        this.loadCutoff();
        this.loadLastUpload();
    },
    computed: {
        isLedgerOnly() {
            return (
                !!this.cutoffDate &&
                moment(this.cutoffDate).format("YYYY-MM-DD") === LEDGER_ONLY_CUTOFF
            );
        },
    },
    methods: {
        loadCutoff() {
            this.loadingCutoff = true;
            getLedgerCutoff(this.idCompany)
                .then(({ data }) => {
                    this.cutoffDate = data.ledger_cutoff_date
                        ? moment(data.ledger_cutoff_date, "YYYY-MM-DD").toDate()
                        : null;
                })
                .catch((error) => {
                    consoleHandlerError({
                        error,
                        customDefaultMessage: "No se pudo leer la fecha de corte del mayor",
                        empty: true,
                    });
                })
                .finally(() => {
                    this.loadingCutoff = false;
                });
        },
        persistCutoff(value) {
            this.savingCutoff = true;
            return setLedgerCutoff(this.idCompany, value)
                .then(({ data }) => {
                    this.cutoffDate = data.ledger_cutoff_date
                        ? moment(data.ledger_cutoff_date, "YYYY-MM-DD").toDate()
                        : null;
                    notificate("Fecha de corte del mayor actualizada", "is-success is-light");
                })
                .catch((error) => {
                    consoleHandlerError({
                        error,
                        customDefaultMessage: "No se pudo guardar la fecha de corte del mayor",
                        empty: true,
                    });
                })
                .finally(() => {
                    this.savingCutoff = false;
                });
        },
        saveCutoff() {
            const value = this.cutoffDate
                ? moment(this.cutoffDate).format("YYYY-MM-DD")
                : null;
            this.persistCutoff(value);
        },
        clearCutoff() {
            this.persistCutoff(null);
        },
        setLedgerOnly() {
            this.cutoffDate = moment(LEDGER_ONLY_CUTOFF, "YYYY-MM-DD").toDate();
            this.persistCutoff(LEDGER_ONLY_CUTOFF);
        },
        loadLastUpload() {
            getLedgerLastUpload(this.idCompany)
                .then(({ data }) => {
                    this.lastUpload = data;
                    if (data.business_super_type) {
                        this.businessType = data.business_super_type;
                    }
                })
                .catch((error) => {
                    consoleHandlerError({
                        error,
                        customDefaultMessage: "No se pudo leer la última subida del mayor",
                        empty: true,
                    });
                });
        },
        formatDateTime(value) {
            return value ? moment(value).format("DD/MM/YYYY HH:mm") : "";
        },
        onUploaded(file) {
            this.fileLedger = file;
        },
        onRemovedFile() {
            this.fileLedger = null;
        },
        submitLedger() {
            if (!this.fileLedger || this.uploadingLedger) return;
            this.uploadingLedger = true;
            uploadLedger(this.idCompany, this.fileLedger, this.businessType)
                .then(({ data }) => {
                    notificate(
                        `<strong>Mayor importado</strong><br>${data.documents_created} documentos creados de ${data.rows_parsed} filas.`,
                        "is-success is-light"
                    );
                    // Refresca el bloque "Última subida" con esta importación.
                    this.lastUpload = {
                        last_ledger_upload_at: data.last_ledger_upload_at,
                        last_ledger_documents_created: data.documents_created,
                        last_ledger_rows_parsed: data.rows_parsed,
                        business_super_type: data.business_super_type,
                    };
                    this.fileLedger = null;
                    this.$emit("imported", data);
                })
                .catch((error) => {
                    const data = error.response && error.response.data;
                    if (data && data.missing_levels && data.missing_levels.length) {
                        notificate(
                            `<strong>Faltan niveles 1/2 en el catálogo</strong><br>${data.missing_levels.join(", ")}`,
                            "is-danger is-light"
                        );
                    } else {
                        consoleHandlerError({
                            error,
                            customDefaultMessage: (data && data.error) || "No se pudo importar el mayor",
                            empty: true,
                        });
                    }
                })
                .finally(() => {
                    this.uploadingLedger = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.last-upload-box {
    border: 1px solid var(--Gray-200, #eaecf0);
    background-color: var(--Gray-50, #f9fafb);
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 16px;
}

.last-upload-title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--report-text-muted-soft, #9ca3af);
}

.last-upload-date {
    font-size: 13px;
    font-weight: 600;
    color: var(--Gray-700, #344054);
    margin-top: 2px;
}

.last-upload-stats {
    display: flex;
    gap: 16px;
    margin-top: 4px;
    font-size: 12px;
    color: var(--report-text-muted, #6b7280);

    strong {
        color: var(--report-primary, #0d3c3f);
    }
}
</style>
